import { db } from '../services/firebase';
import { doc, getDoc, updateDoc, setDoc } from 'firebase/firestore';

/**
 * Script para asegurar que un usuario tenga rol de administrador
 * Este script se puede ejecutar desde la consola del navegador
 */

/**
 * Asegura que un usuario tenga rol de administrador
 * @param {string} userId - ID del usuario
 * @returns {Promise<boolean>} - true si se configuró correctamente, false si no
 */
export const setAdminRole = async (userId) => {
  if (!userId) {
    console.error('❌ No se proporcionó un ID de usuario');
    return false;
  }

  try {
    console.log(`🔧 Configurando rol de administrador para usuario: ${userId}`);
    
    const userRef = doc(db, 'users', userId);
    const userDoc = await getDoc(userRef);
    
    if (userDoc.exists()) {
      // El usuario existe, actualizar su rol
      await updateDoc(userRef, {
        role: 'admin',
        isAdmin: true,
        updatedAt: new Date()
      });
      
      console.log(`✅ Usuario ${userId} actualizado a rol admin`);
    } else {
      // El usuario no existe, crearlo con rol admin
      await setDoc(userRef, {
        role: 'admin',
        isAdmin: true,
        email: `admin-${userId.substring(0, 6)}@paradise-landscaping.com`,
        displayName: 'System Administrator',
        createdAt: new Date(),
        updatedAt: new Date()
      });
      
      console.log(`✅ Usuario ${userId} creado con rol admin`);
    }
    
    return true;
  } catch (error) {
    console.error('❌ Error configurando rol de administrador:', error);
    return false;
  }
};

/**
 * Verifica si un usuario tiene rol de administrador
 * @param {string} userId - ID del usuario
 * @returns {Promise<boolean>} - true si es administrador, false si no
 */
export const checkAdminRole = async (userId) => {
  if (!userId) {
    console.error('❌ No se proporcionó un ID de usuario');
    return false;
  }

  try {
    console.log(`🔍 Verificando rol de administrador para usuario: ${userId}`);
    
    const userRef = doc(db, 'users', userId);
    const userDoc = await getDoc(userRef);
    
    if (userDoc.exists()) {
      const userData = userDoc.data();
      const isAdmin = userData.role === 'admin' || userData.isAdmin === true;
      
      console.log(`👤 Usuario ${userId}: ${isAdmin ? 'ES administrador ✅' : 'NO es administrador ❌'}`);
      console.log('📋 Datos del usuario:', userData);
      
      return isAdmin;
    } else {
      console.log(`❌ Usuario ${userId} no existe en la base de datos`);
      return false;
    }
  } catch (error) {
    console.error('❌ Error verificando rol de administrador:', error);
    return false;
  }
};

/**
 * Función para ejecutar desde la consola del navegador
 * @param {string} userId - ID del usuario
 */
export const fixAdminRole = async (userId) => {
  if (!userId) {
    console.error('❌ Debes proporcionar un ID de usuario');
    console.log('Ejemplo: fixAdminRole("JlSANylhk7YUCSX5wj9sfuYRf6B3")');
    return;
  }
  
  // Verificar si ya es admin
  const isAdmin = await checkAdminRole(userId);
  
  if (isAdmin) {
    console.log(`✅ El usuario ${userId} ya tiene rol de administrador`);
    return true;
  }
  
  // Configurar como admin
  const result = await setAdminRole(userId);
  
  if (result) {
    console.log(`🎉 Usuario ${userId} configurado correctamente como administrador`);
    
    // Verificar nuevamente
    await checkAdminRole(userId);
    return true;
  } else {
    console.error(`❌ No se pudo configurar el usuario ${userId} como administrador`);
    return false;
  }
};

// Exportar funciones para uso global
window.fixAdminRole = fixAdminRole;
window.checkAdminRole = checkAdminRole;
window.setAdminRole = setAdminRole;
